import { CourseCohortDetailsTooltip } from '@/modules/applications/components/ListApplicationsTable/components/CourseCohortDetailsTooltip';
import { CourseDetailStrip } from '@/modules/applications/components/ListApplicationsTable/CourseDetailStrip';
import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { getDefaultMRTOptions } from '@/modules/common/lib/react-material-table';
import { useIndividualPayments } from '@/modules/payments/hooks/useIndividualPayments';
import { IPaymentWithOrder, IPaymentWithUser } from '@/types/payment';
import { IPaymentGroup } from '@/types/payment-groups';
import { Refresh } from '@mui/icons-material';
import { Button, Chip, Skeleton, Typography } from '@mui/material';
import { format, isValid } from 'date-fns';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';

type PaymentGroupDetailPanel = {
  paymentGroup: IPaymentGroup;
};

type PaymentRowData = IPaymentWithUser & IPaymentWithOrder;

const createPaymentTableColumns = (): MRT_ColumnDef<PaymentRowData>[] => [
  {
    accessorKey: 'totalAmount',
    header: 'Amount',
    maxSize: 132,
    Cell: ({ row }) => {
      const payment = row.original;
      return (
        <div className="flex gap-2 items-center">
          <Typography color="primary" variant="body1">
            {formatCurrencyValue(
              payment.totalAmount.toString(),
              payment.currency,
            )}
          </Typography>
        </div>
      );
    },
  },
  {
    accessorKey: 'course',
    header: 'Course Details',
    maxSize: 176,
    Cell: ({ row }) => {
      const payment = row.original;
      const course = payment.order.item.course;
      const cohortId = payment.order.item.cohort;
      const cohort = course.cohorts?.find(c => c._id === cohortId);

      if (!cohort) {
        return null;
      }

      return (
        <CourseCohortDetailsTooltip course={course} cohort={cohort}>
          <div className="flex flex-col gap-1 w-full break-words text-wrap">
            <Typography
              className="text-mydra-purple font-semibold"
              variant="caption"
            >
              <a
                className="cursor-pointer hover:underline"
                href={`/marketplace/courses/${course._id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {course?.name}
              </a>
            </Typography>
            <Typography className="text-xs" variant="caption">
              {course?.educationProvider?.name}
            </Typography>
            <CourseDetailStrip course={course} cohort={cohort} />
          </div>
        </CourseCohortDetailsTooltip>
      );
    },
  },
  {
    accessorKey: 'employee',
    header: 'Employee',
    maxSize: 200,
    Cell: ({ row }) => {
      const payment = row.original;
      return (
        <Typography className="capitalize">
          {[payment.user.firstName, payment.user.lastName]
            .filter(Boolean)
            .join(' ')}
        </Typography>
      );
    },
  },
  {
    accessorKey: 'paymentType',
    header: 'Type',
    size: 180,
    Cell: ({ row }) => {
      const payment = row.original;

      return (
        <div className="flex flex-col flex-1 items-start gap-2">
          <Chip
            label={payment.paymentResource?.replace('_', ' ') || 'N/A'}
            className="capitalize"
            size="small"
          />
        </div>
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Date',
    size: 150,
    Cell: ({ row }) => {
      const payment = row.original;

      return (
        <Typography>
          {isValid(new Date(payment.createdAt)) &&
            format(payment.createdAt, 'Pp')}
        </Typography>
      );
    },
  },
];

export function PaymentGroupDetailPanel({
  paymentGroup,
}: PaymentGroupDetailPanel) {
  const paymentIds = paymentGroup.payments;
  const {
    data: payments = [],
    isLoading: isLoadingPayments,
    refetch: refetchPayments,
  } = useIndividualPayments(paymentIds, ['order', 'user', 'course']);

  const rowData: PaymentRowData[] = paymentIds
    .map(paymentId => payments.find(({ _id }) => _id === paymentId))
    .filter((payment): payment is PaymentRowData => payment !== undefined);

  const defaultMRTOptions = getDefaultMRTOptions<PaymentRowData>();

  const table = useMaterialReactTable<PaymentRowData>({
    ...defaultMRTOptions,
    columns: createPaymentTableColumns(),
    data: rowData,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    renderEmptyRowsFallback: () => (
      <div className="flex flex-col justify-center p-3 h-full rounded-lg items-center align-center text-mydra-black">
        <Typography variant="body2" color="text.secondary">
          No payments found
        </Typography>
      </div>
    ),
    muiTableProps: {
      sx: {
        borderRadius: 0,
        paddingInline: 0,
        padding: 0,
        margin: 0,
        backgroundColor: 'transparent !important',
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: '400px',
        padding: 0,
        margin: 0,
        backgroundColor: 'transparent !important',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        backgroundColor: 'transparent !important',
        padding: 0,
        margin: 0,
        boxShadow: 'none',
      },
    },
    muiTableBodyProps: {
      sx: {
        padding: 0,
        margin: 0,
        backgroundColor: 'transparent !important',
      },
    },
    muiTableHeadProps: {
      sx: {
        backgroundColor: 'transparent !important',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: 'transparent !important',
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: 'transparent !important',
        '&:last-child td': {
          borderBottom: 'none !important',
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: 'transparent !important',
        borderColor: '#E5E7EB',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        textTransform: 'none !important',
        backgroundColor: 'transparent !important',
        borderColor: '#E5E7EB',
      },
    },
  });

  if (!paymentIds.length) {
    return null;
  }

  return (
    <div className="py-2 bg-gray-100">
      {isLoadingPayments ? (
        <div className="space-y-2">
          {paymentIds.map((_, index) => (
            <Skeleton key={index} variant="text" width="100%" height={24} />
          ))}
        </div>
      ) : (
        <>
          {rowData.length === 0 && payments.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <Typography color="error">
                Some payments failed to load
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                color="error"
                onClick={() => {
                  refetchPayments();
                }}
              >
                Retry
              </Button>
            </div>
          )}
          <MaterialReactTable table={table} />
        </>
      )}
    </div>
  );
}
